import { get, identity, isNull, map, noop, range, toNumber } from 'lodash';

import { AccountManager, DocumentsManager } from '@apollo/core';
import moment from 'moment';
import { createPayment, createWithdrawal, passwordResetWithForm } from './api';
import {
  DEPOSIT_PROCESSING_TYPE,
  FIELD_COMPONENTS,
  FIELD_RULES,
  WITHDRAWAL_PROCESSING_TYPE,
} from './constants';

export const getDepositForm = (paymentMethod) => {
  const { tokens, depositType } = paymentMethod;

  let form = null;
  switch (depositType) {
    case DEPOSIT_PROCESSING_TYPE.FORM_POST:
      form = {
        number: 1,
        name: 'payment_deposit',
        autocomplete: true,
        // todo: this should come from API so that it can be toggled Test/Live
        // action: 'https://www.ipg-online.com/connect/gateway/processing',
        action: 'https://test.ipg-online.com/connect/gateway/processing',
        fields: [
          {
            number: 0,
            name: 'authenticateTransaction',
            component: 'hidden',
            value: 'true',
          },

          {
            number: 0,
            name: 'checkoutoption',
            component: 'hidden',
            value: 'combinedpage',
          },

          {
            number: 0,
            name: 'currency',
            component: 'hidden',
            value: '036', // https://docs.firstdata.com/org/gateway/node/56
          },

          {
            number: 0,
            name: 'full_bypass',
            component: 'hidden',
            value: 'true',
          },
          {
            number: 0,
            name: 'hash_algorithm',
            component: 'hidden',
            value: 'HMACSHA256',
          },

          // This is to save a new card
          {
            number: 0,
            name: 'assignToken',
            component: 'hidden',
            value: 'false',
          },

          // This is sent to re-use a stored card
          {
            number: 0,
            name: 'hosteddataid',
            component: 'hidden',
            value: '',
          },

          {
            number: 0,
            name: 'merchantTransactionId',
            component: 'hidden',
            value: '',
          },

          {
            number: 0,
            name: 'oid',
            component: 'hidden',
            value: '',
          },
          {
            number: 0,
            name: 'responseFailURL',
            component: 'hidden',
            value: '',
          },
          {
            number: 0,
            name: 'responseSuccessURL',
            component: 'hidden',
            value: '',
          },

          {
            number: 0,
            name: 'storename',
            component: 'hidden',
            value: '3600001196',
          },

          {
            number: 0,
            name: 'timezone',
            component: 'hidden',
            value: 'Australia/Sydney',
          },

          {
            number: 0,
            name: 'txndatetime',
            component: 'hidden',
            value: moment().format('YYYY:MM:DD-HH:mm:ss'),
          },

          {
            number: 0,
            name: 'txntype',
            component: 'hidden',
            value: 'sale',
          },

          {
            number: 0,
            name: 'hashExtended',
            component: 'hidden',
            value: '',
          },
          {
            number: 2,
            name: 'paymentMethod',
            component: 'select',
            label: 'payment_method',
            placeholder: 'payment_method',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
            required: true,
            translateOptions: false,
            options: [
              {
                text: 'Visa',
                value: 'V',
              },
              {
                text: 'Mastercard',
                value: 'M',
              },
            ],
          },
          {
            number: 3,
            type: 'tel', // allow Leading Zeros
            name: 'cardnumber',
            component: 'TEXT',
            label: 'card_number',
            placeholder: 'card_number',
            required: true,
            value: '',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
              {
                type: 'NUMERIC',
                message: 'numbers_only',
              },
            ],
          },
          {
            number: 4,
            name: 'expmonth',
            component: 'select',
            label: 'expiry_month',
            placeholder: 'expiry_month',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
            required: true,
            translateOptions: false,
            // note:  _.range does not include the end value, so we set to 13
            options: range(1, 13).map((v) => {
              const val = v < 10 ? `0${v}` : v.toString();
              return {
                text: val,
                value: val,
              };
            }),
          },
          {
            number: 5,
            name: 'expyear',
            component: 'select',
            label: 'expiry_year',
            placeholder: 'expiry_year',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
            required: true,
            translateOptions: false,
            options: range(0, 7).map((v) => {
              const val = new Date().getFullYear() + v;
              return {
                text: val,
                value: val,
              };
            }),
          },
          {
            number: 6,
            type: 'tel', // allow Leading Zeros
            name: 'cvm',
            component: 'TEXT',
            label: 'card_cvn',
            placeholder: 'card_cvn',
            required: true,
            value: '',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
              {
                type: 'NUMERIC',
                message: 'numbers_only',
              },
            ],
          },
          {
            number: 7,
            name: 'saveMyDataForLater',
            component: 'CHECKBOX',
            label: 'save_for_later',
            placeholder: 'save_for_later',
          },

          {
            number: 8,
            name: 'chargetotal',
            component: 'money',
            label: 'payment_amount',
            placeholder: 'payment_amount',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
            required: true,
            value: '',
          },
          {
            number: 9,
            name: 'actionButtons',
            component: 'actionButtons',
            applyTo: 'chargetotal',
            options: [10, 25, 50, 100, 500],
            className: 'action-buttons',
            action: (applyToValue, value) => applyToValue + value * 100,
          },
        ],
      };
      break;

    case DEPOSIT_PROCESSING_TYPE.POPUP:
    case DEPOSIT_PROCESSING_TYPE.REDIRECT:
    case DEPOSIT_PROCESSING_TYPE.NONE:
    default:
      form = {
        number: 1,
        name: 'payment_deposit',
        autocomplete: false,
        action: '',
        fields: [
          {
            number: 1,
            name: 'amount',
            component: 'money',
            label: 'payment_amount',
            placeholder: 'payment_amount',
            required: true,
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
          },
          {
            number: 2,
            name: 'actionButtons',
            component: 'actionButtons',
            applyTo: 'amount',
            options: [10, 25, 50, 100, 500],
            className: 'action-buttons',
            action: (applyToValue, value) => applyToValue + value * 100,
          },
        ],
      };
      break;
  }

  if (tokens && tokens.length) {
    const tokenSelectOptions = map(tokens, (token) => {
      const { name } = token;
      return {
        text: name,
        value: name,
      };
    });

    tokenSelectOptions.unshift({
      text: '-',
      value: null,
    });

    form.fields.unshift({
      number: 1,
      component: 'select',
      name: 'accountName',
      label: 'payment_token',
      placeholder: 'payment_token',
      translateOptions: false,
      rules: [
        {
          type: 'REQUIRED',
          message: 'required_field',
        },
      ],
      options: tokenSelectOptions,
    });
  }

  return form;
};

export const getWithdrawalForm = (paymentMethod) => {
  const { tokens, tokenRequired, withdrawalType } = paymentMethod;
  let form = null;
  switch (withdrawalType) {
    case WITHDRAWAL_PROCESSING_TYPE.EFT:
      form = {
        number: 1,
        name: 'payment_withdrawal',
        autocomplete: true,
        fields: [
          {
            number: 2,
            name: 'accountName',
            component: 'text',
            label: 'account_name',
            placeholder: 'account_name',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
          },
          {
            number: 3,
            name: 'bsbNumber',
            component: 'text',
            label: 'bsb_number',
            placeholder: 'bsb_number',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
          },
          {
            number: 4,
            name: 'accountNumber',
            component: 'text',
            label: 'account_number',
            placeholder: 'account_number',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
          },
          {
            number: 5,
            name: 'saveMyDataForLater',
            component: 'CHECKBOX',
            label: 'save_for_later',
            placeholder: 'save_for_later',
          },
          {
            number: 6,
            name: 'metaId',
            component: 'hidden',
            value: '',
          },
          {
            component: 'money',
            name: 'amount',
            number: 7,
            label: 'payment_amount',
            placeholder: 'payment_amount',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
          },
          {
            number: 8,
            name: 'actionButtons',
            component: 'actionButtons',
            applyTo: 'amount',
            options: [10, 25, 50, 100, 500],
            className: 'action-buttons',
            action: (applyToValue, value) => applyToValue + value * 100,
          },
        ],
      };
      break;

    case WITHDRAWAL_PROCESSING_TYPE.REDIRECT:
    case WITHDRAWAL_PROCESSING_TYPE.POPUP:
    case WITHDRAWAL_PROCESSING_TYPE.NONE:
    default:
      form = {
        number: 1,
        name: 'payment_withdrawal',
        autocomplete: false,
        fields: [
          {
            component: 'money',
            name: 'amount',
            number: 1,
            label: 'payment_amount',
            placeholder: 'payment_amount',
            rules: [
              {
                type: 'REQUIRED',
                message: 'required_field',
              },
            ],
          },
        ],
      };
      break;
  }

  if (!tokenRequired) {
    return form;
  }

  const tokenField = {
    number: 0,
    component: 'select',
    name: 'accountName',
    label: 'payment_token',
    placeholder: 'payment_token',
    translateOptions: false,
    rules: [
      {
        type: 'REQUIRED',
        message: 'required_field',
      },
    ],
    options: [],
  };

  if (tokens && tokens.length) {
    tokenField.options = map(tokens, (token) => {
      const { name } = token;

      return {
        text: name,
        value: name,
      };
    });
  } else {
    tokenField.disabled = true;
  }
  // form.fields.unshift(tokenField)

  console.log(form.fields);
  return form;
};

export const getUploadDocumentForm = (documentTypes) => {
  const { SELECT, FILE } = FIELD_COMPONENTS;
  const { REQUIRED } = FIELD_RULES;

  return {
    name: 'uploadDocument',
    fields: [
      {
        component: SELECT,
        name: 'documentType',
        options: map(documentTypes, (documentType) => ({
          text: documentType,
          value: documentType,
        })),
        value: get(documentTypes, '[0]', null),
        rules: [{ type: REQUIRED, message: 'Select the document type' }],
      },
      {
        component: FILE,
        placeholder: 'Drag and Drop or Click here',
        name: 'file',
        label: '',
        rules: [
          {
            type: 'REQUIRED',
            message: 'Select the file',
          },
        ],
      },
    ],
  };
};

export const generateErrorFromCode = (
  code,
  message = code,
  violationInfo = {},
) => ({
  type: code,
  message,
  violationInfo,
});

export const handleFormError = (error, setFormError = noop) => {
  const {
    message,
    code,
    status,
    violationInfo,
    // errors
  } = error;

  // if (errors && errors.length) {
  // TODO: Implement multierrors format
  // }

  if (code === 'USER_IS_LOCKED') {
    setFormError([generateErrorFromCode(code, message, violationInfo)]);
  } else {
    setFormError([
      generateErrorFromCode(code || status, message || code, violationInfo),
    ]);
  }

  throw error;
};

export const handleFormSubmit = (action, dataFormatter = identity) => async (
  formData,
  { setGlobalErrors, setIsLoading },
) => {
  const formattedData = dataFormatter(formData);
  if (isNull(formattedData)) {
    return;
  }

  try {
    setIsLoading(true);
    const result = await action(formattedData);
    setIsLoading(false);

    return result;
  } catch (error) {
    setIsLoading(false);
    handleFormError(error, setGlobalErrors);
  }
};

export const onUploadDocumentSubmit = handleFormSubmit(
  DocumentsManager.uploadDocument.bind(DocumentsManager),
  (data) => {
    const formData = new window.FormData();
    const { file, documentType } = data;

    formData.append('file', file);
    formData.append('documentType', documentType);

    return formData;
  },
);

export const onDepositSubmit = handleFormSubmit(createPayment, (props) => {
  const {
    amount,
    chargetotal,
    pmId,
    accountName,
    cardnumber,
    isCordova,
  } = props;

  const data = {
    amount: toNumber(amount || chargetotal),
    pmId,
    gatewayData: JSON.stringify({
      ...props,

      isCordova,

      // Mask Card Number
      cardnumber: cardnumber
        ? String(cardnumber).replace(/.(?=.{4})/g, '#')
        : undefined,

      // Remove sensitive fields
      // Add more as required
      expmonth: undefined,
      expyear: undefined,
      cvm: undefined,
    }),
  };

  if (accountName) {
    data.accountName = accountName;
  }

  return data;
});

export const onWithdrawalSubmit = handleFormSubmit(
  createWithdrawal,
  (props) => {
    const { amount, pmId, cardnumber, isCordova } = props;
    const processedAmount = toNumber(amount);
    return {
      amount: processedAmount,
      pmId,
      gatewayData: JSON.stringify({
        ...props,

        isCordova,

        // Mask Card Number
        cardnumber: cardnumber
          ? String(cardnumber).replace(/.(?=.{4})/g, '#')
          : undefined,

        // Remove sensitive fields
        // Add more as required
        expmonth: undefined,
        expyear: undefined,
        cvm: undefined,
      }),
    };
  },
);

export const onSignUpSubmit = handleFormSubmit(
  AccountManager.formRegistation.bind(AccountManager),
);

export const onForgotPasswordSubmit = handleFormSubmit(
  AccountManager.initPasswordResetWithForm.bind(AccountManager),
);

export const onSignInSubmit = handleFormSubmit(
  AccountManager.loginWithForm.bind(AccountManager),
);

export const onProfileUpdateSubmit = handleFormSubmit(
  AccountManager.profileUpdateWithForm.bind(AccountManager),
);

export const onChangePasswordSubmit = handleFormSubmit(
  AccountManager.changePasswordWithForm.bind(AccountManager),
);

export const onPasswordResetSubmit = handleFormSubmit(
  AccountManager.passwordResetWithForm.bind(AccountManager),
);

export const onPasswordResetPhoneSubmit = handleFormSubmit(
  passwordResetWithForm,
);
